import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  Scissors,
  Wind,
  Droplet,
  Dumbbell,
  Heart,
  Apple,
  ChevronLeft,
  Send,
  ArrowDown,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

// Nova Coach — single-thread chat with a system-primed GPT-5.2 coach.
// FREE for launch. Topics are pre-defined chips that bias the followups.
// The coach is conversational: short replies, asks ONE follow-up per turn,
// quick-reply chips push the convo forward like real texting.

// Launch spec (2026-02-26): Hair + Confidence coaches removed for v1.
// Focus is on the four most practical coaches.
const TOPICS = [
  { id: "skin",       label: "Skin",       icon: Droplet },
  { id: "fitness",    label: "Fitness",    icon: Dumbbell },
  { id: "nutrition",  label: "Nutrition",  icon: Apple },
  { id: "cologne",    label: "Cologne",    icon: Wind },
];

// Short, tag-style quick replies (taps drive the conversation forward).
const TOPIC_OPENERS = {
  cologne:    ["Fresh", "Warm", "Spicy", "Under $100", "Date night", "Office"],
  skin:       ["Oily", "Dry", "Acne", "Sensitive", "AM routine", "PM routine"],
  fitness:    ["Lose weight", "Build muscle", "Beginner", "Gym", "Home", "3 days/week"],
  nutrition:  ["Lose weight", "Build muscle", "More energy", "Quick meals", "Vegetarian", "Eating out"],
};

// First-message hints when a user opens a topic — short, conversational prompts.
const TOPIC_GREETINGS = {
  cologne:    "Fresh, warm, or spicy? And what's the occasion?",
  skin:       "How's your skin feeling lately — oily, dry, or in between?",
  fitness:    "What's the goal — lose weight, build muscle, or feel better?",
  nutrition:  "What's the goal — fat loss, muscle, or just more energy?",
};

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`coach-bubble-row ${isUser ? "user" : "assistant"}`} data-testid={`coach-msg-${msg.role}`}>
      {!isUser && (
        <div className="coach-avatar" aria-hidden="true">
          <Sparkles size={14} strokeWidth={2}/>
        </div>
      )}
      <div className={`coach-bubble ${isUser ? "user" : "assistant"}`}>
        {msg.text.split("\n").map((line, i) => (
          <p key={i}>{line}</p>
        ))}
      </div>
    </div>
  );
}

export default function CoachScreen() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [topic, setTopic] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [followups, setFollowups] = useState(TOPIC_OPENERS.skin);
  const [kbHeight, setKbHeight] = useState(0);   // px of keyboard overlap
  const [inputFocused, setInputFocused] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Keyboard-aware composer. Subscribes to window.visualViewport on iOS Safari
  // + Chrome Android; on resize/scroll we compute the on-screen-keyboard
  // overlap = window.innerHeight - vv.height - vv.offsetTop, and store it in
  // state (NOT a CSS variable — inline style is more reliable on iOS WKWebView
  // where CSS-var propagation can lag). Also writes --kb-h on documentElement
  // so the BottomNav can hide itself when the keyboard is up.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const recalc = () => {
      const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKbHeight(overlap);
      document.documentElement.style.setProperty("--kb-h", `${overlap}px`);
    };
    recalc();
    vv.addEventListener("resize", recalc);
    vv.addEventListener("scroll", recalc);
    // iOS Safari sometimes fires the resize ~600ms AFTER the keyboard fully
    // animates. Re-check a few times after focus to catch the late fire.
    return () => {
      vv.removeEventListener("resize", recalc);
      vv.removeEventListener("scroll", recalc);
      document.documentElement.style.setProperty("--kb-h", "0px");
      document.body.classList.remove("kb-open");
    };
  }, []);

  // Toggle body class so the BottomNav can hide itself when the keyboard is up,
  // OR whenever the input is focused (a focus-driven fallback for browsers
  // where visualViewport doesn't fire reliably, e.g. older iOS WebViews).
  // Also: hide the BottomNav for the ENTIRE /coach route lifetime so the
  // iMessage-style docked composer can sit at the very bottom of the screen
  // without sandwiching the nav between itself and the conversation.
  useEffect(() => {
    document.body.classList.add("coach-route");
    return () => {
      document.body.classList.remove("coach-route");
      document.body.classList.remove("kb-open");
    };
  }, []);

  useEffect(() => {
    if (kbHeight > 0 || inputFocused) {
      document.body.classList.add("kb-open");
    } else {
      document.body.classList.remove("kb-open");
    }
  }, [kbHeight, inputFocused]);

  // When the input focuses, give the keyboard time to animate up, then scroll
  // the freshest message + the input itself into view. iOS is 250–600 ms.
  const onInputFocus = () => {
    setInputFocused(true);
    // Re-sample visualViewport at multiple delays — iOS Safari can fire its
    // resize event up to ~600ms AFTER focus, so a single setTimeout often
    // misses it. Three samples cover the full animation envelope.
    const recheck = () => {
      const vv = window.visualViewport;
      if (vv) {
        const overlap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        setKbHeight(overlap);
      }
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
      inputRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    };
    setTimeout(recheck, 120);
    setTimeout(recheck, 350);
    setTimeout(recheck, 700);
  };
  const onInputBlur = () => {
    setInputFocused(false);
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || sending) return;
    const userMsg = { role: "user", text: value, id: `u-${Date.now()}` };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/coach/chat", {
        message: value,
        session_id: sessionId || undefined,
        topic: topic || undefined,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply, id: `a-${Date.now()}` }]);
      setFollowups(data.suggested_followups || []);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: formatApiError(err) || "Couldn't reach the coach. Try again.", id: `err-${Date.now()}`, error: true },
      ]);
    } finally { setSending(false); }
  };

  const openers = useMemo(() => (topic ? TOPIC_OPENERS[topic] : TOPIC_OPENERS.skin), [topic]);
  const showWelcome = messages.length === 0;

  return (
    <div className="screen with-nav fade-in coach-screen-wrapper" data-testid="coach-screen">
      <div className="top-bar">
        <button
          type="button" className="icon-btn"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="coach-back-btn"
        >
          <ChevronLeft size={18}/>
        </button>
        <div className="logo-mark" data-testid="coach-logo">COACH</div>
        <span className="premium-pill" data-testid="coach-free-pill">FREE</span>
      </div>

      <div className="eyebrow" style={{ marginBottom: 8 }}>YOUR NOVA COACH</div>
      <h2 className="h1" style={{ fontSize: 28, marginBottom: 18, lineHeight: 1.12 }}>
        Ask anything.<br/>
        <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>Real answers.</em>
      </h2>

      {/* Topic chips */}
      <div className="coach-topics" data-testid="coach-topics">
        {TOPICS.map((t) => {
          const Icon = t.icon;
          const active = topic === t.id;
          return (
            <button
              key={t.id}
              type="button"
              className={`coach-topic ${active ? "active" : ""}`}
              onClick={() => setTopic(active ? null : t.id)}
              data-testid={`coach-topic-${t.id}`}
            >
              <Icon size={13} strokeWidth={1.8}/>
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Chat thread */}
      <div className="coach-thread" ref={scrollRef} data-testid="coach-thread">
        {showWelcome && (
          <div className="coach-welcome" data-testid="coach-welcome">
            <div className="coach-avatar large"><Sparkles size={20} strokeWidth={1.8}/></div>
            <p className="body" style={{ color: "#fff", textAlign: "center", marginTop: 14 }} data-testid="coach-welcome-greeting">
              {topic ? TOPIC_GREETINGS[topic] : "Pick a topic above, or just ask me anything."}
            </p>
            <div className="coach-opener-list">
              {openers.map((q, i) => (
                <button
                  key={i}
                  type="button"
                  className="coach-opener"
                  onClick={() => send(q)}
                  data-testid={`coach-opener-${i}`}
                >
                  {q} <ArrowDown size={12} strokeWidth={2} style={{ transform: "rotate(-90deg)" }}/>
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => <MessageBubble key={m.id} msg={m} />)}
        {sending && (
          <div className="coach-bubble-row assistant" data-testid="coach-typing">
            <div className="coach-avatar"><Sparkles size={14} strokeWidth={2}/></div>
            <div className="coach-bubble assistant typing">
              <span className="dot-anim"/><span className="dot-anim"/><span className="dot-anim"/>
            </div>
          </div>
        )}
      </div>

      {/* Quick followups after a reply */}
      {!showWelcome && !sending && followups?.length > 0 && (
        <div className="coach-followups" data-testid="coach-followups">
          {followups.slice(0, 6).map((q, i) => (
            <button
              key={i}
              type="button"
              className="coach-followup"
              onClick={() => send(q)}
              data-testid={`coach-followup-${i}`}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Composer — iMessage-style: always docked at the bottom of the visible
          viewport. `bottom` tracks the visualViewport keyboard overlap so it
          rides above the keyboard exactly. z-index 10000 sits above .glow-nav. */}
      <div
        className="coach-composer"
        data-testid="coach-composer"
        data-kb-open={(kbHeight > 0 || inputFocused) ? "true" : "false"}
        style={{
          // Two states only:
          //   • kbHeight > 0: keyboard is open → match its height exactly.
          //   • otherwise: parked at the very bottom of the viewport,
          //     just above the safe-area inset. No nav offset — the nav is
          //     hidden whenever the user is on /coach via body.coach-route.
          bottom: kbHeight > 0
            ? `${kbHeight}px`
            : `env(safe-area-inset-bottom, 0px)`,
          zIndex: 10000,
        }}
      >
        <input
          ref={inputRef}
          className="input"
          placeholder="Ask the coach…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          data-testid="coach-input"
          autoComplete="off"
        />
        <button
          type="button"
          className="btn btn-gold"
          onClick={() => send()}
          disabled={!input.trim() || sending}
          data-testid="coach-send-btn"
          style={{ padding: "12px 18px" }}
        >
          <Send size={16} strokeWidth={2}/>
        </button>
      </div>
    </div>
  );
}
